import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useKeycloak } from '@react-keycloak/web';
import { toast } from 'react-toastify';
import { listPageUrl, page_size } from 'api/apiUtils';
import { queryKeys } from 'api/queryKeys';
import { createSource, getSourcesForRoute } from 'api/sources/sourcesApi';
import { useDestinationUrls } from 'api/useFilterOptions';
import { useListQuery } from 'api/useListQuery';
import { Source } from 'components/common/ObjectTypes';
import { changeSourceGlobalSearch } from 'components/sources/SourceHelperFunctions';
import type { RouteSourceProps } from '../RouteDetailsSources';

type FilterTags = {
  name: string;
  id: string;
};
type SourceFilterTypes = {
  destination_url__in: FilterTags[];
  enabled: FilterTags[];
  type__in: FilterTags[];
  owner__in: FilterTags[];
  ordering: string;
  search: string;
};

// What the page supplies; RouteDetailsSource gets the rest from this hook.
export type RouteSourcesTabProps = Omit<RouteSourceProps, 'pageLoaded'>;

const toSingleSourceValue = (tags: FilterTags[]) =>
  tags.length > 0 ? { label: tags[0].name, value: tags[0].id } : null;

// The Sources tab's state: its filter (kept for the session), its page, the
// sources query and every handler RouteDetailsSource calls. The page calls
// this hook (rather than the tab mounting it) so the page and filter survive
// switching to another tab and back.
export const useRouteSourcesTab = ({
  routeId,
  providerId,
  active,
  pageLoaded,
}: {
  routeId: string;
  providerId: string | undefined;
  // The Sources tab is the open one.
  active: boolean;
  // The Route has loaded.
  pageLoaded: boolean;
}): RouteSourcesTabProps => {
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const navigate = useNavigate();

  const [sourceFilter, setSourceFilter] = useState<SourceFilterTypes>(() => {
    const savedFilter = sessionStorage.getItem('routeSourceFilter');
    return savedFilter
      ? JSON.parse(savedFilter)
      : {
          destination_url__in: [],
          enabled: [],
          type__in: [],
          owner__in: [],
          // Alphabetical by default, matching the device-filter editor's list.
          ordering: 'external_id',
          search: '',
        };
  });
  const [sourcesOffset, setSourcesOffset] = useState<number>(0);
  // The Sources tab loads the first time it's opened, then follows its filters.
  const [sourcesTabOpened, setSourcesTabOpened] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('routeSourceFilter', JSON.stringify(sourceFilter));
  }, [sourceFilter]);

  useEffect(() => {
    if (pageLoaded && active) setSourcesTabOpened(true);
  }, [pageLoaded, active]);

  const routeSourcesQuery = useMemo(
    () => ({
      search: sourceFilter.search,
      destination_url__in: sourceFilter.destination_url__in
        .map((a) => a.id)
        .toString(),
      ordering: sourceFilter.ordering,
      offset: sourcesOffset,
      previous: listPageUrl('/sources/', sourcesOffset - page_size),
      next: listPageUrl('/sources/', sourcesOffset + page_size),
    }),
    [sourceFilter, sourcesOffset],
  );
  const routeSources = useListQuery<Source>(
    queryKeys.routes.sources(routeId, providerId ?? '', routeSourcesQuery),
    (token) => getSourcesForRoute(token, providerId ?? '', routeSourcesQuery),
    pageLoaded && sourcesTabOpened && !!providerId,
  );

  // Waits for the Route itself so the filter options don't compete with it.
  const { destinationUrls, loadingDestinationUrls } = useDestinationUrls({
    enabled: pageLoaded,
  });

  const handleChangeGlobalSourceSearch = (searchSubStr: string) => {
    setSourceFilter((prev) => changeSourceGlobalSearch(searchSubStr, prev));
    setSourcesOffset(0);
  };

  const handleSelectSourceUrlFilter = (selected: any) => {
    const tags = selected ? [{ name: selected.label, id: selected.value }] : [];
    setSourceFilter((prev) => ({ ...prev, destination_url__in: tags }));
    setSourcesOffset(0);
  };

  const handleSortSources = (selected: { label: string; value: string }) => {
    setSourceFilter((prev) => ({ ...prev, ordering: selected.value }));
    setSourcesOffset(0);
  };

  const handleSourcePageClick = (e: any) => {
    setSourcesOffset(e.selected * page_size);
  };

  // Opening one of the Route's destinations selects it on the flow map.
  const handleViewDestination = (destinationId: string) =>
    navigate(`/routes/${routeId}`, {
      replace: true,
      state: {
        flowSelection: { kind: 'account', integrationId: destinationId },
      },
    });

  // Adding a device by hand from the Sources tab. The provider is the route's own,
  // the same one the rules editor writes against.
  const handleCreateRouteSource = async (payload: {
    external_id: string;
    name?: string;
  }) => {
    const token = keycloak.token;
    // The drawer closes on resolve, so a quiet return here would discard the typing
    // while implying the device was created.
    if (!providerId || !token || typeof token !== 'string') {
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
      throw new Error('Source create preconditions not met');
    }
    await createSource(token, { provider: providerId, ...payload });
    toast.success(`${t('Source added')}`, {
      position: toast.POSITION.TOP_RIGHT,
    });
    routeSources.refresh();
  };

  return {
    sources: routeSources.results,
    totalSources: routeSources.count,
    globalSearchStr: sourceFilter.search,
    take: page_size,
    offset: sourcesOffset,
    onPageClicked: handleSourcePageClick,
    loadingSources: routeSources.loading,
    destinationUrls,
    loadingDestinationUrls,
    destinationUrlFilterValue: toSingleSourceValue(
      sourceFilter.destination_url__in,
    ),
    onChangeGlobalSourceSearch: handleChangeGlobalSourceSearch,
    onRefresh: () => routeSources.refresh(),
    onChangeDestinationUrlFilter: handleSelectSourceUrlFilter,
    onViewDestination: handleViewDestination,
    ordering: sourceFilter.ordering,
    onChangeSortBy: handleSortSources,
    onCreateSource: handleCreateRouteSource,
  };
};
